//Transactions.tsx
'use client';

import { useState, useEffect } from 'react';
import {
  ExternalLink,
  MoreHorizontal,
  ArrowUpDown,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Image from 'next/image';
import { Tooltip } from '@/components/ui/tooltip';
import { useDashboard } from '@/contexts/DashboardContext';

interface Transaction {
  id?: string;
  image: string;
  date: string;
  title: string;
  price: number;
  seller?: string;
  buyer?: string;
  marketplace: string;
  url?: string;
  transactionId?: string;
  sourceLink?: string;
  poster?: string;
}

interface SymbolInfo {
  name: string;
  description?: string;
  exchange?: string;
  price?: number;
}

interface TransactionsProps {
  selectedSymbol: SymbolInfo;
  mobile?: boolean;
}

export function Transactions({
  selectedSymbol,
  mobile = false,
}: TransactionsProps) {
  const { state } = useDashboard();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  // Calculate statistics
  const [stats, setStats] = useState({
    avgPrice: 0,
    priceRange: { min: 0, max: 0 },
    transactionCount: 0,
  });

  // Load transactions data
  useEffect(() => {
    let isMounted = true;

    const fetchTransactions = async () => {
      if (!selectedSymbol?.name) return;

      // Reset error state
      setError(null);

      // Only show loading on initial load, not refreshes
      if (!isRefreshing) {
        setIsLoading(true);
      }

      // Check if it's an economic indicator or index
      const isEconomicIndicator = [
        'METAL:',
        'INDEX:',
        'INFLATION:',
        'INTEREST:',
      ].some((prefix) => selectedSymbol.name.startsWith(prefix));

      if (isEconomicIndicator) {
        if (isMounted) {
          setError(
            'Transactions are not available for economic indicators or indices.'
          );
          setTransactions([]);
          setIsLoading(false);
          setIsRefreshing(false);
        }
        return;
      }

      try {
        console.log(
          `Fetching transactions for ${selectedSymbol.name} with source ${state.source}`
        );

        // Fetch data from API with current source
        const response = await fetch(
          `/api/trading-dashboard/getRecentTransactions?symbol=${encodeURIComponent(selectedSymbol.name)}&source=${state.source}`
        );

        if (!response.ok) {
          throw new Error(`API responded with status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Transactions data:', data);

        if (isMounted) {
          if (Array.isArray(data)) {
            // Map API response to our transaction model
            const formattedTransactions: Transaction[] = data.map(
              (item: any, index) => ({
                id: item.transactionId || `trx-${index}-${Date.now()}`,
                image: item.image || '/placeholder.svg',
                date: item.date,
                title: item.title || `${selectedSymbol.name.replace(':', ' ')}`,
                price: item.price,
                seller: item.poster || 'Unknown',
                buyer: item.buyer || 'Unknown',
                marketplace: item.marketplace,
                sourceLink: item.sourceLink || '#',
                transactionId: item.transactionId,
                poster: item.poster,
              })
            );

            setTransactions(formattedTransactions);

            // Calculate statistics
            if (formattedTransactions.length > 0) {
              const prices = formattedTransactions.map((t) => t.price);
              const avgPrice =
                prices.reduce((sum, price) => sum + price, 0) / prices.length;
              const minPrice = Math.min(...prices);
              const maxPrice = Math.max(...prices);

              setStats({
                avgPrice: Math.round(avgPrice),
                priceRange: { min: minPrice, max: maxPrice },
                transactionCount: formattedTransactions.length,
              });
            } else {
              setStats({
                avgPrice: 0,
                priceRange: { min: 0, max: 0 },
                transactionCount: 0,
              });
            }
          } else if (data.error) {
            setError(data.error);
            setTransactions([]);
          } else {
            setError('Unexpected data format received from API');
            setTransactions([]);
          }
        }
      } catch (err) {
        console.error('Error fetching transactions:', err);
        if (isMounted) {
          setError('Failed to load transaction data. Please try again later.');
          setTransactions([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    };

    fetchTransactions();

    return () => {
      isMounted = false;
    };
  }, [selectedSymbol?.name, state.source, isRefreshing]);

  // Handle image loading errors
  const handleImageError = (imageUrl: string) => {
    setFailedImages((prev) => new Set(prev).add(imageUrl));
  };

  // Format date string
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(date);
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Get marketplace badge color
  const getMarketplaceColor = (marketplace: string) => {
    const normalizedMarketplace = marketplace.toLowerCase();

    switch (true) {
      case normalizedMarketplace.includes('ebay'):
        return 'bg-blue-600/20 text-blue-400';
      case normalizedMarketplace.includes('chrono'):
        return 'bg-green-600/20 text-green-400';
      case normalizedMarketplace.includes('reddit'):
        return 'bg-orange-600/20 text-orange-400';
      case normalizedMarketplace.includes('rolex'):
      case normalizedMarketplace.includes('forum'):
        return 'bg-purple-600/20 text-purple-400';
      case normalizedMarketplace.includes('watch'):
        return 'bg-yellow-600/20 text-yellow-400';
      case normalizedMarketplace.includes('all'):
        return 'bg-blue-600/20 text-blue-400';
      default:
        return 'bg-slate-600/20 text-slate-400';
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    setIsRefreshing(true);
  };
  return (
    <div className="flex flex-col bg-transparent">


      {/* Header */}
      <div className="py-3 px-4 border-b border-slate-800 bg-gradient-to-r from-slate-900 to-slate-800/90 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center">
          <ArrowUpDown className="h-4 w-4 mr-2 text-blue-400" />
          <h3 className="text-sm font-semibold text-white">
            Recent Price Transactions
          </h3>
        </div>

        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className="text-xs bg-blue-600/10 border-blue-500/30 text-blue-400"
          >
            {selectedSymbol.name.split(':')[1] || 'All Models'}
          </Badge>

          <Tooltip content="Refresh transactions">
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-7 p-0 text-slate-300 border-slate-700"
              onClick={handleRefresh}
              disabled={isRefreshing || isLoading}
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`}
              />
            </Button>
          </Tooltip>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-px bg-slate-800/50 border-b border-slate-800 flex-shrink-0">
        <div className="bg-slate-900 py-2 px-3 text-center">
          <p className="text-slate-400 text-xs">Avg. Price</p>
          <p className="text-white font-medium">
            {stats.avgPrice > 0 ? `$${stats.avgPrice.toLocaleString()}` : 'N/A'}
          </p>
        </div>
        <div className="bg-slate-900 py-2 px-3 text-center">
          <p className="text-slate-400 text-xs">Transactions</p>
          <p className="text-white font-medium">
            {stats.transactionCount} total
          </p>
        </div>
        <div className="bg-slate-900 py-2 px-3 text-center">
          <p className="text-slate-400 text-xs">Price Range</p>
          <p className="text-white font-medium">
            {stats.priceRange.min > 0
              ? `$${stats.priceRange.min.toLocaleString()}-$${stats.priceRange.max.toLocaleString()}`
              : 'N/A'}
          </p>
        </div>
      </div>

      {/* Transaction List */}
      <div className="flex-1 flex flex-col">

        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-32 px-4 text-center">
            <AlertCircle className="h-8 w-8 text-slate-500 mb-2" />
            <div className="text-slate-400">{error}</div>
          </div>
        ) : transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32">
            <AlertCircle className="h-8 w-8 text-slate-500 mb-2" />
            <div className="text-slate-400">
              No transactions found for this model
            </div>
          </div>
        ) : (
          <div className="min-w-full">
            <table className="min-w-full divide-y divide-slate-800/50">
              <thead className="bg-slate-900/70 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Item
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Price
                  </th>
                  {!mobile && (
                    <>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                        Source
                      </th>
                    </>
                  )}
                  <th className="px-3 py-2.5 text-right text-xs font-medium text-slate-400 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="bg-slate-900/20 divide-y divide-slate-800/30">
                {transactions.map((transaction, index) => (
                  <tr
                    key={
                      transaction.id ||
                      transaction.transactionId ||
                      `${transaction.date}-${index}`
                    }
                    className="hover:bg-slate-800/40 transition-colors duration-150"
                  >
                    <td className="px-3 py-3 whitespace-nowrap text-xs text-slate-300">
                      <span className="bg-slate-800/50 px-2 py-1 rounded">
                        {formatDate(transaction.date)}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 relative rounded-md overflow-hidden bg-slate-800 border border-slate-700/50">
                          {!failedImages.has(transaction.image) ? (
                            <Image
                              src={
                                transaction.image.startsWith('/')
                                  ? transaction.image
                                  : `/api/trading-dashboard/proxy-image?url=${encodeURIComponent(transaction.image)}`
                              }
                              alt={transaction.title}
                              fill
                              className="object-cover"
                              onError={() =>
                                handleImageError(transaction.image)
                              }
                            />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center text-slate-600">
                              <ExternalLink className="h-5 w-5" />
                            </div>
                          )}
                        </div>
                        <div className="ml-3">
                          <div className="text-xs font-medium text-white line-clamp-2 max-w-[200px]">
                            {transaction.title}
                          </div>
                          <div className="text-xs text-slate-400 flex items-center">
                            <Badge
                              variant="outline"
                              className="mr-1 px-1 py-0 text-[10px] border-slate-700/50"
                            >
                              {mobile
                                ? transaction.marketplace
                                : transaction.seller ||
                                transaction.poster ||
                                'Unknown'}
                            </Badge>
                            {mobile && (
                              <span className="text-[10px] text-slate-500 truncate max-w-[80px]">
                                {transaction.seller ||
                                  transaction.poster ||
                                  'Unknown'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-xs font-semibold text-white bg-blue-500/10 px-2 py-1 rounded-md border border-blue-500/20 inline-block">
                        ${transaction.price.toLocaleString('en-US')}
                      </div>
                    </td>
                    {!mobile && (
                      <td className="px-3 py-3 whitespace-nowrap">
                        <Badge
                          variant="secondary"
                          className={`text-xs ${getMarketplaceColor(transaction.marketplace)} border-none px-2 py-1`}
                        >
                          {transaction.marketplace}
                        </Badge>
                      </td>
                    )}
                    <td className="px-3 py-3 whitespace-nowrap text-right text-xs font-medium">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 rounded-full hover:bg-slate-800"
                          >
                            <MoreHorizontal className="h-4 w-4 text-slate-400" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="bg-slate-900 border border-slate-700 text-white"
                        >
                          {transaction.sourceLink &&
                            transaction.sourceLink !== '#' &&
                            transaction.sourceLink !== 'CSV Import' && (
                              <DropdownMenuItem
                                className="text-xs hover:bg-slate-800 cursor-pointer"
                                onClick={() =>
                                  window.open(transaction.sourceLink, '_blank')
                                }
                              >
                                <ExternalLink className="h-3.5 w-3.5 mr-2" />
                                View Original Listing
                              </DropdownMenuItem>
                            )}
                          <DropdownMenuItem className="text-xs hover:bg-slate-800 cursor-pointer">
                            Add to Watchlist
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-xs hover:bg-slate-800 cursor-pointer">
                            Add Note
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* "Showing x transactions" now part of the scrollable area */}
            <div className="py-3 px-4 text-xs text-slate-400 bg-slate-900/30 border-t border-slate-800/50">
              Showing {transactions.length} recent transactions
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-slate-800 bg-slate-900/50 flex justify-between items-center flex-shrink-0">
        <div></div> {/* Empty div for spacing */}
        <div className="flex space-x-1">
          <Tooltip content="View all transactions">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-slate-400"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          </Tooltip>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-slate-400"
          >
            Copy
          </Button>
        </div>
      </div>
    </div>
  );
}